import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  Image,
  Button,
} from "@chakra-ui/react";
import CloseIconBlack from "../../../assets/images/booking/x-circle.svg";
import {
  InputControl,
  SelectControl,
} from "../../../components/form/formControls";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { formatPriceUS } from "../../../utils/utils";
import { useCategories } from "../../../categorys/brokerDebtCertificate/index";
import { useMemo } from "react";

const schema = Yup.object().shape({
  employeeId: Yup.string().required("Nhân viên là bắt buộc"),
  amountPaid: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        return originalValue.trim() === "" ? null : Number(originalValue);
      }
      return originalValue === null ? null : Number(originalValue);
    })
    .required("Số tiền đã trả là bắt buộc"),
  amountHeld: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        return originalValue.trim() === "" ? null : Number(originalValue);
      }
      return originalValue === null ? null : Number(originalValue);
    })
    .required("Số tiền tạm giữ là bắt buộc"),
  amountActualRemaining: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        return originalValue.trim() === "" ? null : Number(originalValue);
      }
      return originalValue === null ? null : Number(originalValue);
    })
    .required("Số tiền còn lại là bắt buộc"),
  // Thuế TNCN — chỉ lưu trữ, admin sửa tay được. Không bắt buộc.
  taxTNCN: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        return originalValue.trim() === "" ? null : Number(originalValue);
      }
      return originalValue === null ? null : Number(originalValue);
    }),
});

const AddBrokerDebtCertificate = ({
  isOpen,
  onClose,
  defaultValues,
  isEdit,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: defaultValues || {},
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      if (defaultValues) {
        setDisplayValue(formatPriceUS(defaultValues?.amountPaid || 0));
        setDisplayAmountPaidValue(formatPriceUS(defaultValues?.amountHeld || 0));
        setDisplayAmountActualRemainingValue(formatPriceUS(defaultValues?.amountActualRemaining || 0));
        // Thuế TNCN: prefill giá trị đã lưu, hoặc mặc định 10% × Số tiền còn lại.
        setDisplayTaxTNCNValue(
          formatPriceUS(
            defaultValues?.taxTNCN != null
              ? defaultValues.taxTNCN
              : 0.1 * Number(defaultValues?.amountActualRemaining || 0),
          ),
        );
      }
    }
  }, [isOpen, defaultValues, reset]);

  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFetch(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);
  const { constantData } = useCategories(shouldFetch);

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, value]) => ({
      value: key,
      label: value,
    }));

  const employees = useMemo(
    () => formatEntries(constantData?.employee),
    [constantData?.employee]
  );

  const [focusStates, setFocusStates] = useState({});
  const handleFocus = (field) => {
    setFocusStates((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setFocusStates((prev) => ({ ...prev, [field]: false }));
  };

  const submitHandler = (data) => {
    onSubmit(data);
  };

  // Price
  const [displayValue, setDisplayValue] = useState(0);
  const [displayAmountPaidValue, setDisplayAmountPaidValue] = useState(0);
  const [displayAmountActualRemainingValue, setDisplayAmountActualRemainingValue] = useState(0);
  const [displayTaxTNCNValue, setDisplayTaxTNCNValue] = useState(0);

  const onchangeEmployee = (option) => {
    setValue("employeeId", option);
  };

  const onChangePrice = useCallback(
    (event) => {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, "");
      setDisplayValue(formatPriceUS(numericValue));
      setValue("amountPaid", Number(numericValue), {
        shouldValidate: true,
      });
    },
    [setValue, watch, formatPriceUS]
  );
  const onChangePriceAmountHeld = useCallback(
    (event) => {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, "");
      setDisplayAmountPaidValue(formatPriceUS(numericValue));
      setValue("amountHeld", Number(numericValue), {
        shouldValidate: true,
      });
    },
    [setValue, watch, formatPriceUS]
  );

  const onChangePriceAmountActualRemaining = useCallback(
    (event) => {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, "");
      setDisplayAmountActualRemainingValue(formatPriceUS(numericValue));
      setValue("amountActualRemaining", Number(numericValue), {
        shouldValidate: true,
      });
    },
    [setValue, watch, formatPriceUS]
  );

  // Thuế TNCN — admin nhập tay; chỉ lưu trữ, không tính toán.
  const onChangePriceTaxTNCN = useCallback(
    (event) => {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, "");
      setDisplayTaxTNCNValue(formatPriceUS(numericValue));
      setValue("taxTNCN", Number(numericValue), {
        shouldValidate: true,
      });
    },
    [setValue, watch, formatPriceUS]
  );
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        h={"525px"}
        w={"100%"}
        maxW={"625px"}
        overflowY={"auto"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
          "&::-moz-scrollbar": {
            width: "4px",
          },
          "&::-moz-scrollbar-thumb": {
            width: "4px",
            borderRadius: "50px",
            background: "var(--color-background)",
          },
        }}
      >
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex minH={"525px"} flexDirection={"column"}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
              padding={"25px 24px"}
              borderBottom={"1px solid var(--bg-line-employee)"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                {isEdit ? "Sửa tạm giữ CCMG" : "Tạo tạm giữ CCMG"}
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <Image src={CloseIconBlack} />
              </Box>
            </Flex>
            <Box flex={1} w={"545px"} m={"24px auto"}>
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"16px"}
              >
                <SelectControl
                  label="Nhân viên"
                  dataOption={employees}
                  placeholder="Chọn nhân viên"
                  selectedMain={watch("employeeId")}
                  onFocus={() => handleFocus("employeeId")}
                  onBlur={() => handleBlur("employeeId")}
                  state={focusStates["employeeId"]}
                  onChange={onchangeEmployee}
                  register={register("employeeId")}
                  errorMessage={errors.employeeId}
                  isRequired={true}
                  isSearchable={true}
                  mwidth={"100%"}
                />
              </Flex>
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"16px"}
              >
                <InputControl
                  // type="number"
                  label="Số tiền tạm giữ"
                  placeholder="Ví dụ: 195000000000 "
                  errors={errors.amountHeld}
                  onFocus={() => handleFocus("amountHeld")}
                  onBlur={() => handleBlur("amountHeld")}
                  onChange={onChangePriceAmountHeld}
                  value={displayAmountPaidValue}
                  state={focusStates["amountHeld"]}
                  register={register("amountHeld")}
                  mwidth={"100%"}
                  isRequired={true}
                />
                <InputControl
                  // type="number"
                  label="Số tiền đã trả"
                  placeholder="Ví dụ: 195000000000 "
                  errors={errors.amountPaid}
                  onFocus={() => handleFocus("amountPaid")}
                  onBlur={() => handleBlur("amountPaid")}
                  onChange={onChangePrice}
                  value={displayValue}
                  state={focusStates["amountPaid"]}
                  register={register("amountPaid")}
                  mwidth={"100%"}
                  isRequired={true}
                />
              </Flex>
              <Flex
                justifyContent={"space-between"}
                alignItems={"flex-start"}
                gap={"27px"}
                mb={"16px"}
              >
                <InputControl
                  // type="number"
                  label="Số tiền còn lại"
                  placeholder="Ví dụ: 195000000000 "
                  errors={errors.amountActualRemaining}
                  onFocus={() => handleFocus("amountActualRemaining")}
                  onBlur={() => handleBlur("amountActualRemaining")}
                  onChange={onChangePriceAmountActualRemaining}
                  value={displayAmountActualRemainingValue}
                  state={focusStates["amountActualRemaining"]}
                  register={register("amountActualRemaining")}
                  mwidth={"100%"}
                  isRequired={true}
                />
                <InputControl
                  // type="number"
                  label="Thuế TNCN"
                  placeholder="Mặc định 10% × Số tiền còn lại"
                  errors={errors.taxTNCN}
                  onFocus={() => handleFocus("taxTNCN")}
                  onBlur={() => handleBlur("taxTNCN")}
                  onChange={onChangePriceTaxTNCN}
                  value={displayTaxTNCNValue}
                  state={focusStates["taxTNCN"]}
                  register={register("taxTNCN")}
                  mwidth={"100%"}
                />
              </Flex>
            </Box>
            <Flex p={"24px"} justifyContent={"flex-end"}>
              {isEdit ? (
                <Flex gap={"16px"}>
                  <Button
                    p={"13px 16px 13px 20px"}
                    fontWeight={600}
                    fontSize={"15px"}
                    lineHeight={"18px"}
                    color={"var(--color-option-employee-hover)"}
                    border={"1px solid var(--color-option-employee-hover)"}
                    bg={"var(--color-main)"}
                    onClick={onClose}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                  >
                    Hủy
                  </Button>
                  <Button
                    type="submit"
                    p={"13px 16px 13px 20px"}
                    fontWeight={600}
                    fontSize={"15px"}
                    lineHeight={"18px"}
                    color={"var(--color-main)"}
                    bg={"var(--color-button)"}
                    _hover={{
                      filter: "brightness(90%)",
                    }}
                  >
                    Lưu
                  </Button>
                </Flex>
              ) : (
                <Button
                  type="submit"
                  p={"13px 16px 13px 20px"}
                  fontWeight={600}
                  fontSize={"15px"}
                  lineHeight={"18px"}
                  color={"var(--color-main)"}
                  bg={"var(--color-button)"}
                  _hover={{
                    filter: "brightness(90%)",
                  }}
                >
                  Tạo tạm giữ CCMG
                </Button>
              )}
            </Flex>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddBrokerDebtCertificate;
